import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UserData, getCurrentUser, TokenResponse } from '../data/userApi';

interface AuthContextType {
  isAuthenticated: boolean;
  isVerified: boolean;
  user: UserData | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (token: TokenResponse) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // User authentication state
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('auth_token');
  });
  
  const [user, setUser] = useState<UserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Effect for user token
  const fetchUserData = useCallback(async () => {
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setIsVerified(false);
      setLoading(false); 
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const userData = await getCurrentUser(token);
      setUser(userData);
      setIsVerified(userData.is_verified);
      
      // Only set authenticated if the user is verified
      setIsAuthenticated(userData.is_verified);
      
      // If user is not verified, set an appropriate error
      if (!userData.is_verified) {
        setError('Please verify your email to access your account');
        // Keep the token so they can verify their account
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Session expired or invalid. Please log in again.'); // More specific error
      // Token might be expired or invalid
      setToken(null); // Clear token on fetch error
      setUser(null);
      setIsAuthenticated(false);
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
    // Fetch user data whenever the token changes (login/logout)
    fetchUserData();
  }, [token, fetchUserData]);

  // Set token from login
  const login = async (tokenResponse: TokenResponse) => {
    setToken(tokenResponse.access_token);
    // No need to fetch user data here, the useEffect[token] handles it.
    // Reset error state when a new login is attempted
    setError(null);
    setLoading(true); // Loading will be set to false by fetchUserData
  };

  // Logout function
  const logout = () => {
    setToken(null);
    // State updates for user, isAuthenticated, isVerified will happen in useEffect[token]
  };

  const value = {
    isAuthenticated,
    isVerified,
    user,
    token,
    loading,
    error,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 